import { Paginator } from '@crehana/compass.ui/paginator';
import {
  Table,
  TableMainHeader,
  TableSearch,
  TableTitleAndCounter,
  TableTop,
} from '@crehana/compass.ui/table';
import { useTable } from '@crehana/compass.ui/table/tanstack';
import { clsx } from 'clsx';
import { PREVIEW_TABLE } from '../../../constants';
import { useAddResourceBundle } from '../../../i18n/use-add-resource-bundle';
import { useTranslationCareerSiteAdminShared } from '../../../i18n/use-translations';
import type { TTableVacanciesList } from '../shared/types';
import { DepartmentFilter } from './department-filter';
import type { TVacanciesCardListProps } from './types';
import { VacanciesCardContent } from './vacancies-card-content';

export const VacanciesCardList = ({
  data,
  loading,
  error,
  search,
  columnFilters,
  pagination,
  onSearchChange,
  onColumnFiltersChange,
  onPaginationChange,
  onRefetch,
  title,
  color,
  previewType,
  rowCount,
  departmentsData,
  totalItems,
  cardBackgroundClassName,
}: TVacanciesCardListProps) => {
  useAddResourceBundle();
  const { t } = useTranslationCareerSiteAdminShared();

  const table = useTable<TTableVacanciesList>({
    data,
    columns: [],
    rowCount,
    onSearchChange,
    onPaginationChange,
    onColumnFiltersChange,
    state: {
      pagination,
      columnFilters,
      search,
    },
  });

  const showPaginator =
    !loading &&
    table.getRowCount() > 0 &&
    totalItems &&
    totalItems > pagination.pageSize;

  return (
    <div className={clsx('tw-w-full tw-h-full', cardBackgroundClassName)}>
      <Table
        className={clsx(
          'tw-border-separate tw-border-spacing-0 tw-w-full tw-border-none tw-px-0',
          cardBackgroundClassName,
        )}
      >
        <TableTop className={clsx('tw-px-0', cardBackgroundClassName)}>
          <TableMainHeader className="tw-flex tw-flex-col md:tw-flex-row tw-justify-start tw-items-center md:tw-justify-between tw-px-0">
            <div
              className={clsx(
                'tw-w-full tw-flex tw-gap-2 tw-flex-col md:tw-flex-row',
                previewType === PREVIEW_TABLE.MOBILE && 'tw-flex-col',
              )}
            >
              <TableTitleAndCounter
                count={table.getRowCount()}
                title={title || ''}
              />
              <TableSearch
                className="tw-bg-neutral-light-100"
                placeholder={t('SEARCH_BY_NAME')}
                onSearch={table.searchRows}
              />
            </div>
            {departmentsData && (
              <DepartmentFilter
                departmentsData={departmentsData}
                previewType={previewType}
                onClear={() => {
                  table.setColumnFilters([]);
                  table.setPageIndex(0);
                }}
                onSelect={value => {
                  table.setColumnFilters([{ id: 'area', value }]);
                  table.setPageIndex(0);
                }}
              />
            )}
          </TableMainHeader>
        </TableTop>
      </Table>

      <div className="tw-w-full">
        <VacanciesCardContent
          data={data}
          loading={loading}
          error={error}
          search={search}
          pageSize={table.pageSize}
          color={color}
          previewType={previewType}
          onRefetch={onRefetch}
        />
      </div>

      {showPaginator && (
        <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-mt-4">
          <Paginator
            variant="numeric"
            totalPages={Math.ceil(rowCount / table.pageSize)}
            onPageChange={page => table.setPageIndex(page - 1)}
            currentPage={table.currentPage + 1}
          />
        </div>
      )}
    </div>
  );
};
